import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useToast } from '../components/Toast';

/* ── Types ─────────────────────────────────────────────────────────── */

interface LeaveBalance {
  AnnualDays: number;
  UsedDays: number;
  Adjustment: number;
  RemainingDays: number;
}

interface LeaveRequest {
  RequestId: number;
  EmployeeUpn: string;
  StartDate: string;
  EndDate: string;
  LeaveType: string;
  BusinessDays: number;
  Reason: string | null;
  Status: string;
  ReviewedBy: string | null;
  ReviewedAt: string | null;
  CreatedAt: string;
  AttachmentCount?: number;
}

interface LeaveAttachment {
  AttachmentId: number;
  RequestId: number;
  FileName: string;
  ContentType: string;
  FileSize: number;
  CreatedAt: string | null;
}

interface PublicHoliday {
  HolidayId: number;
  Name: string;
  HolidayDate: string;
}

const leaveTypes = [
  { value: 'annual', label: 'Annual Leave' },
  { value: 'sick', label: 'Sick Leave' },
  { value: 'family', label: 'Family Responsibility' },
  { value: 'study', label: 'Study Leave' },
  { value: 'unpaid', label: 'Unpaid Leave' },
  { value: 'other', label: 'Other' },
];

const statusColors: Record<string, string> = {
  pending: '#f59e0b',
  approved: '#10b981',
  rejected: '#ef4444',
  cancelled: '#6b7280',
};

const approvalStageLabel = (status: string): string => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'pending') return 'Pending manager review';
  if (normalized === 'approved') return 'Approved';
  if (normalized === 'rejected') return 'Declined';
  if (normalized === 'cancelled') return 'Cancelled';
  return status || '-';
};

const ALLOWED_ATTACHMENT_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx'];
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

const badgeStyle = (status: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: 12,
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#fff',
  background: statusColors[status] || '#6b7280',
});

/* ── Component ─────────────────────────────────────────────────────── */

export default function LeaveApplicationPage() {
  const api = useApi();
  const qc = useQueryClient();
  const { user } = useAuth();
  const toast = useToast();
  const currentYear = new Date().getFullYear();

  // Form state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [leaveType, setLeaveType] = useState('annual');
  const [reason, setReason] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentViewRequestId, setAttachmentViewRequestId] = useState<number | null>(null);
  const [attachmentViewRows, setAttachmentViewRows] = useState<LeaveAttachment[]>([]);
  const [attachmentViewLoading, setAttachmentViewLoading] = useState(false);

  // Data queries
  const { data: balance } = useQuery<LeaveBalance>({
    queryKey: ['my-leave-balance', currentYear],
    queryFn: () => api.get(`/leave/my-balance?year=${currentYear}`),
  });

  const { data: requests = [] } = useQuery<LeaveRequest[]>({
    queryKey: ['my-leave-requests', currentYear, user?.upn],
    queryFn: () => api.get(`/leave/requests?year=${currentYear}${user?.upn ? `&employee_upn=${encodeURIComponent(user.upn)}` : ''}`),
  });

  const { data: holidays = [] } = useQuery<PublicHoliday[]>({
    queryKey: ['public-holidays', currentYear],
    queryFn: () => api.get(`/leave/holidays?year=${currentYear}`),
  });

  // Mutations
  const submitMut = useMutation({
    mutationFn: (formData: FormData) => api.postForm('/leave/requests', formData),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-leave-requests'] });
      qc.invalidateQueries({ queryKey: ['my-leave-balance'] });
      toast.success('Leave request submitted');
      setStartDate('');
      setEndDate('');
      setLeaveType('annual');
      setReason('');
      setAttachments([]);
    },
    onError: (e: any) => toast.error(e.message || 'Failed to submit'),
  });

  const cancelMut = useMutation({
    mutationFn: (id: number) => api.del(`/leave/requests/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-leave-requests'] });
      qc.invalidateQueries({ queryKey: ['my-leave-balance'] });
      toast.success('Request cancelled');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to cancel'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate || !endDate) return;
    const formData = new FormData();
    formData.append('startDate', startDate);
    formData.append('endDate', endDate);
    formData.append('leaveType', leaveType);
    formData.append('reason', reason || '');
    for (const file of attachments) {
      formData.append('attachments', file, file.name);
    }
    submitMut.mutate(formData);
  };

  const onAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    const valid: File[] = [];
    for (const file of picked) {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      if (!ALLOWED_ATTACHMENT_EXTENSIONS.includes(ext)) {
        toast.error(`Unsupported type: ${file.name}`);
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`File too large (${file.name}) - max 10MB each`);
        continue;
      }
      valid.push(file);
    }
    setAttachments(prev => {
      const dedupe = new Set(prev.map(f => `${f.name}:${f.size}:${f.lastModified}`));
      const merged = [...prev];
      for (const file of valid) {
        const key = `${file.name}:${file.size}:${file.lastModified}`;
        if (!dedupe.has(key)) {
          merged.push(file);
          dedupe.add(key);
        }
      }
      return merged;
    });
    // Allow selecting the same file again after removal.
    e.target.value = '';
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const toggleRequestAttachments = async (requestId: number) => {
    if (attachmentViewRequestId === requestId) {
      setAttachmentViewRequestId(null);
      setAttachmentViewRows([]);
      setAttachmentViewLoading(false);
      return;
    }
    await loadRequestAttachments(requestId);
  };

  const loadRequestAttachments = async (requestId: number) => {
    setAttachmentViewRequestId(requestId);
    setAttachmentViewLoading(true);
    try {
      const rows = (await api.get(`/leave/requests/${requestId}/attachments`)) as LeaveAttachment[];
      setAttachmentViewRows(rows);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load attachments');
      setAttachmentViewRows([]);
    } finally {
      setAttachmentViewLoading(false);
    }
  };

  const downloadAttachment = async (attachment: LeaveAttachment) => {
    try {
      const blob = await api.getBlob(`/leave/attachments/${attachment.AttachmentId}/download`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.FileName || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to download attachment');
    }
  };

  // Helper: check if a date is a public holiday
  const holidaySet = new Set(holidays.map(h => h.HolidayDate));
  const isHoliday = (dateStr: string) => holidaySet.has(dateStr);

  // Count business days for preview
  const countPreviewDays = () => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (end < start) return null;
    let days = 0;
    const cur = new Date(start);
    while (cur <= end) {
      const dow = cur.getDay();
      const iso = cur.toISOString().slice(0, 10);
      if (dow !== 0 && dow !== 6 && !isHoliday(iso)) days++;
      cur.setDate(cur.getDate() + 1);
    }
    return days;
  };

  const previewDays = countPreviewDays();

  const cardStyle: React.CSSProperties = {
    background: '#fff',
    borderRadius: 12,
    padding: 24,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    marginBottom: 20,
  };

  const balanceColor = (remaining: number) =>
    remaining < 0 ? '#ef4444' : remaining <= 3 ? '#f59e0b' : '#10b981';

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 24, fontFamily: 'inherit' }}>
      <h2 style={{ marginBottom: 6 }}>Leave Application</h2>
      <p style={{ color: '#6b7280', marginBottom: 24, fontSize: '0.9rem' }}>
        Apply for leave, view your balance, and track your requests.
      </p>

      {/* ── Balance summary ─────────────────────────────────────── */}
      {balance && (
        <div style={{
          ...cardStyle,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 16,
          textAlign: 'center',
        }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>Annual Days</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{balance.AnnualDays}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>Used</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{balance.UsedDays}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>Adjustment</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{balance.Adjustment}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>Remaining</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: balanceColor(balance.RemainingDays) }}>
              {balance.RemainingDays}
            </div>
          </div>
        </div>
      )}

      {/* ── Application form ────────────────────────────────────── */}
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, marginBottom: 16 }}>New Leave Request</h3>
        <form onSubmit={handleSubmit}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.85rem', marginBottom: 16 }}>
            Supporting documents
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={onAttachmentChange}
              style={{ fontSize: '0.9rem' }}
            />
            <span style={{ color: '#6b7280', fontSize: '0.78rem' }}>
              Allowed: PDF, JPG/JPEG, PNG, DOC, DOCX (max 10MB each)
            </span>
            {attachments.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ color: '#2563eb', fontSize: '0.78rem', fontWeight: 600 }}>
                  {attachments.length} attachment{attachments.length !== 1 ? 's' : ''} selected
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {attachments.map((file, idx) => (
                    <div key={`${file.name}:${file.size}:${file.lastModified}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.78rem', color: '#374151' }}>
                        {file.name} ({(file.size / 1024).toFixed(0)} KB)
                      </span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(idx)}
                        style={{
                          padding: '2px 8px',
                          borderRadius: 6,
                          border: '1px solid #ef4444',
                          background: '#fff',
                          color: '#ef4444',
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.85rem' }}>
              Start Date
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                required
                style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #d0d5dd', fontSize: '0.9rem' }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.85rem' }}>
              End Date
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                min={startDate || undefined}
                required
                style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #d0d5dd', fontSize: '0.9rem' }}
              />
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.85rem' }}>
              Leave Type
              <select
                value={leaveType}
                onChange={e => setLeaveType(e.target.value)}
                style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #d0d5dd', fontSize: '0.9rem' }}
              >
                {leaveTypes.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </label>
            {previewDays !== null && (
              <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 4 }}>
                <span style={{
                  fontSize: '0.9rem',
                  padding: '6px 14px',
                  background: '#f0f4ff',
                  borderRadius: 8,
                  fontWeight: 600,
                  color: '#2563eb',
                }}>
                  {previewDays} business day{previewDays !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.85rem', marginBottom: 16 }}>
            Reason (optional)
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={2}
              style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #d0d5dd', fontSize: '0.9rem', resize: 'vertical' }}
            />
          </label>
          <button
            type="submit"
            disabled={submitMut.isPending || !startDate || !endDate}
            style={{
              padding: '10px 28px',
              borderRadius: 8,
              border: 'none',
              background: '#2563eb',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.9rem',
              cursor: submitMut.isPending ? 'wait' : 'pointer',
              opacity: submitMut.isPending ? 0.6 : 1,
            }}
          >
            {submitMut.isPending ? 'Submitting…' : 'Submit Leave Request'}
          </button>
        </form>
      </div>

      {/* ── Public holidays ─────────────────────────────────────── */}
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>Public Holidays ({currentYear})</h3>
        {holidays.length === 0 ? (
          <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>No public holidays listed.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
            {holidays.map(h => (
              <div key={h.HolidayId} style={{
                padding: '8px 12px',
                background: '#fef3c7',
                borderRadius: 8,
                fontSize: '0.85rem',
                display: 'flex',
                justifyContent: 'space-between',
              }}>
                <span style={{ fontWeight: 600 }}>{h.Name}</span>
                <span style={{ color: '#92400e' }}>{new Date(h.HolidayDate + 'T00:00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── My requests ─────────────────────────────────────────── */}
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>My Requests ({currentYear})</h3>
        {requests.length === 0 ? (
          <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>No leave requests yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: '8px 6px' }}>Type</th>
                <th style={{ padding: '8px 6px' }}>Dates</th>
                <th style={{ padding: '8px 6px' }}>Days</th>
                <th style={{ padding: '8px 6px' }}>Status</th>
                <th style={{ padding: '8px 6px' }}>Approval Stage</th>
                <th style={{ padding: '8px 6px' }}>Reason</th>
                <th style={{ padding: '8px 6px', textAlign: 'center' }}>Attachments</th>
                <th style={{ padding: '8px 6px' }}></th>
              </tr>
            </thead>
            <tbody>
              {requests.map(r => (
                <React.Fragment key={r.RequestId}>
                  <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '8px 6px', textTransform: 'capitalize' }}>{r.LeaveType}</td>
                    <td style={{ padding: '8px 6px' }}>
                      {new Date(r.StartDate + 'T00:00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}
                      {' – '}
                      {new Date(r.EndDate + 'T00:00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}
                    </td>
                    <td style={{ padding: '8px 6px', fontWeight: 600 }}>{r.BusinessDays}</td>
                    <td style={{ padding: '8px 6px' }}>
                      <span style={badgeStyle(r.Status)}>{r.Status}</span>
                    </td>
                    <td style={{ padding: '8px 6px', color: '#374151', fontWeight: 500 }}>
                      {approvalStageLabel(r.Status)}
                    </td>
                    <td style={{ padding: '8px 6px', color: '#6b7280', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.Reason || '—'}
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: '0.78rem', color: '#6b7280', minWidth: 18, display: 'inline-block' }}>
                          {r.AttachmentCount || 0}
                        </span>
                        <button
                          type="button"
                          onClick={() => toggleRequestAttachments(r.RequestId)}
                          style={{
                            padding: '4px 10px',
                            borderRadius: 6,
                            border: '1px solid #d1d5db',
                            background: '#fff',
                            color: '#111827',
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {attachmentViewRequestId === r.RequestId ? 'Hide' : 'View'}
                        </button>
                      </div>
                    </td>
                    <td style={{ padding: '8px 6px' }}>
                      {r.Status === 'pending' && (
                        <button
                          onClick={() => cancelMut.mutate(r.RequestId)}
                          disabled={cancelMut.isPending}
                          style={{
                            padding: '4px 12px',
                            borderRadius: 6,
                            border: '1px solid #ef4444',
                            background: '#fff',
                            color: '#ef4444',
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                  {attachmentViewRequestId === r.RequestId && (
                    <tr>
                      <td colSpan={8} style={{ padding: '8px 6px', background: '#f9fafb' }}>
                        {attachmentViewLoading ? (
                          <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>Loading attachments...</span>
                        ) : attachmentViewRows.length === 0 ? (
                          <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>No attachments uploaded.</span>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {attachmentViewRows.map(att => (
                              <div key={att.AttachmentId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem' }}>
                                <span>{att.FileName} ({(att.FileSize / 1024).toFixed(0)} KB)</span>
                                <button
                                  type="button"
                                  onClick={() => downloadAttachment(att)}
                                  style={{
                                    padding: '3px 10px',
                                    borderRadius: 6,
                                    border: '1px solid #2563eb',
                                    background: '#fff',
                                    color: '#2563eb',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    cursor: 'pointer',
                                  }}
                                >
                                  Download
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
